import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import {
  useBillingPortal,
  useBillingStatus,
  useCheckoutReturn,
  useConfirmCheckout,
  useStartCheckout,
} from '@/features/account/api/use-billing';
import { AccessLocked } from '@/features/account/components/access-locked';
import { useBootstrap } from '@/features/auth/api/use-bootstrap';
import { useSignOutAction } from '@/features/auth/use-sign-out-action';
import { ACCESS_REQUIRED_EVENT } from '@/shared/api/client';
import { billingStatusQuery } from '@/shared/api/queries/billing';
import { USAGE_UPDATED_EVENT } from '@/shared/api/usage-events';
import { FullPageSpinner } from '@/shared/ui/spinner';

import type { SignOutOptions } from '@/features/auth/auth-context';
import type { BillingPlanId } from '@mio/shared';

/**
 * Replaces the app with the plan page once paid access has ended. Bootstrap decides the
 * first paint; the billing status query and 402 responses keep it current afterwards.
 */
export function AccessGate({
  learnerId,
  signOut,
  children,
}: {
  learnerId: string | null;
  signOut: (options?: SignOutOptions) => Promise<void>;
  children: ReactNode;
}) {
  const queryClient = useQueryClient();
  const bootstrap = useBootstrap(learnerId);
  const bootstrapLocked = bootstrap.data ? bootstrap.data.access.status !== 'active' : false;
  const billing = useBillingStatus(learnerId !== null);
  useEffect(() => {
    const refresh = () => {
      void queryClient.invalidateQueries({ queryKey: billingStatusQuery.queryKey });
    };
    window.addEventListener(ACCESS_REQUIRED_EVENT, refresh);
    window.addEventListener(USAGE_UPDATED_EVENT, refresh);
    return () => {
      window.removeEventListener(ACCESS_REQUIRED_EVENT, refresh);
      window.removeEventListener(USAGE_UPDATED_EVENT, refresh);
    };
  }, [queryClient]);
  const locked = billing.data ? billing.data.access.locked : bootstrapLocked;
  const checkout = useStartCheckout();
  const confirm = useConfirmCheckout();
  const portal = useBillingPortal();
  const returned = useCheckoutReturn();
  const logout = useSignOutAction(signOut);
  const [busyPlan, setBusyPlan] = useState<BillingPlanId | null>(null);
  const confirmed = useRef(false);
  useEffect(() => {
    if (!locked || confirmed.current || returned.outcome !== 'success' || !returned.sessionId)
      return;
    confirmed.current = true;
    confirm.mutate(returned.sessionId);
  }, [confirm, locked, returned]);
  if (!locked) return children;
  // Bootstrap already knows access ended: never paint the app, wait for the plan status.
  if (!billing.data) return <FullPageSpinner label="Checking your plan…" />;
  return (
    <AccessLocked
      status={billing.data}
      busyPlan={busyPlan}
      confirming={confirm.isPending}
      error={
        checkout.error?.message ??
        portal.error?.message ??
        confirm.error?.message ??
        logout.error ??
        null
      }
      signingOut={logout.busy}
      portalBusy={portal.isPending}
      onSelect={(plan) => {
        setBusyPlan(plan);
        checkout.mutate(plan, {
          onSuccess: (redirect) => window.location.assign(redirect.url),
          onError: () => setBusyPlan(null),
        });
      }}
      onPortal={() =>
        portal.mutate(undefined, { onSuccess: (redirect) => window.location.assign(redirect.url) })
      }
      onSignOut={() => void (logout.canForce ? logout.force() : logout.run())}
    />
  );
}
